import { SelectProvider } from '@ariakit/react'
import type { FC } from 'react'
import { InputHelpText } from '@/components/ui/input-help-text'
import {
	SelectItem,
	SelectList,
	SelectPopover,
	SelectTrigger,
	SelectVirtualList
} from '@/components/ui/select-atoms-legacy'
import { Skeleton } from '@/components/ui/skeleton'
import type {
	Category,
	CategoryFormat
} from '@/features/experience-shell/types.ts'
import { categoryFormatToLabel } from '@/features/experience-shell/utils.ts'
import { useTranslation } from '@/translations/i18n.tsx'
import { useGetExperienceVettingCategoryFormats } from '../queries/use-get-experience-vetting-category-formats'
import { useUpdateCategorySubCategory } from '../queries/use-update-category-sub-category'

const SelectContent = ({
	isLoading,
	isError,
	categoryFormats,
	fetchNextPage
}: {
	isLoading: boolean
	isError: boolean
	categoryFormats: { id: number; keyName: CategoryFormat }[]
	fetchNextPage: () => void
}) => {
	const { t } = useTranslation()
	if (isLoading) {
		return (
			<div className='flex flex-col gap-1'>
				{Array.from({ length: 6 }, (_, i) => `skeleton-${i}`).map(
					(key) => (
						<Skeleton height={22} key={key} />
					)
				)}
			</div>
		)
	}
	if (isError) {
		return <InputHelpText error={t('errors:default')} />
	}
	if (categoryFormats.length === 0) {
		return <InputHelpText helpText={t('common:no_results')} />
	}
	return (
		<SelectVirtualList
			data={categoryFormats}
			endReached={fetchNextPage}
			itemContent={(_, option) => (
				<SelectItem key={option.id} value={option.keyName}>
					{t(categoryFormatToLabel[option.keyName])}
				</SelectItem>
			)}
			overscan={100}
			totalCount={categoryFormats.length}
		/>
	)
}

interface ExpVettingCriteriaCategorySelectProps {
	defaultCategoryFormat: CategoryFormat
	category: Category
	experienceId: number
	disabled?: boolean
}

export const ExpVettingCriteriaCategorySelect: FC<
	ExpVettingCriteriaCategorySelectProps
> = ({ defaultCategoryFormat, category, experienceId, disabled }) => {
	const {
		remappedData: categoryFormats,
		isLoading,
		isError,
		fetchNextPage
	} = useGetExperienceVettingCategoryFormats({
		categoryKeyName: category,
		limit: 100
	})
	const updateCategoryFormat = useUpdateCategorySubCategory({ experienceId })
	const { t } = useTranslation()
	const onChangeCategory = (value: CategoryFormat) => {
		updateCategoryFormat.mutate({
			categoryKeyName: category,
			subCategoryKeyName: value
		})
	}

	return (
		<SelectProvider
			setValue={onChangeCategory}
			value={defaultCategoryFormat}
		>
			<SelectTrigger
				disabled={disabled || updateCategoryFormat.isPending}
				displayValue={() => (
					<>{t(categoryFormatToLabel[defaultCategoryFormat])}</>
				)}
			/>
			<SelectPopover style={{ width: '200px' }}>
				<SelectList>
					<SelectContent
						categoryFormats={categoryFormats}
						fetchNextPage={fetchNextPage}
						isError={isError}
						isLoading={isLoading}
					/>
				</SelectList>
			</SelectPopover>
		</SelectProvider>
	)
}
